import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Grid,
  useTheme,
  useMediaQuery,
  Snackbar
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  DragIndicator as DragIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import adminAuthService from '../services/adminAuthService';
import { API_BASE_URL } from '../config/api';

const BannerManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [banners, setBanners] = useState([]);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    image: '',
    link: '',
    active: true,
    display_order: 0
  });

  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!adminAuthService.isAuthenticated() || !adminAuthService.isAdmin()) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_banners.php`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('فشل في تحميل البانرات');
      }

      const data = await response.json();
      if (data.success) {
        setBanners(data.banners || []);
      } else {
        throw new Error(data.message || 'فشل في تحميل البانرات');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.image;

    try {
      setUploading(true);
      const token = adminAuthService.getToken();
      const formDataUpload = new FormData();
      formDataUpload.append('image', imageFile);
      formDataUpload.append('action', 'upload');

      const response = await fetch(`${API_BASE_URL}/api/admin_banners.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      const data = await response.json();
      if (data.success) {
        return data.image_url;
      } else {
        throw new Error(data.message || 'فشل في رفع الصورة');
      }
    } catch (error) {
      throw new Error('فشل في رفع الصورة: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleCreateBanner = async () => {
    try {
      setError('');
      
      // Upload image if a new one was selected
      let imageUrl = formData.image;
      if (imageFile) {
        imageUrl = await uploadImage();
      }

      if (!imageUrl) {
        throw new Error('يجب رفع صورة للبانر');
      }

      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_banners.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'create',
          ...formData,
          image: imageUrl
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('تم إنشاء البانر بنجاح');
        setDialogOpen(false);
        resetForm();
        loadData();
      } else {
        throw new Error(data.message || 'فشل في إنشاء البانر');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleUpdateBanner = async () => {
    try {
      setError('');
      
      // Upload image if a new one was selected
      let imageUrl = formData.image;
      if (imageFile) {
        imageUrl = await uploadImage();
      }

      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_banners.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update',
          id: selectedBanner.id,
          ...formData,
          image: imageUrl
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('تم تحديث البانر بنجاح');
        setDialogOpen(false);
        resetForm();
        loadData();
      } else {
        throw new Error(data.message || 'فشل في تحديث البانر');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا البانر؟')) {
      return;
    }

    try {
      setError('');
      const token = adminAuthService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/api/admin_banners.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'delete',
          id: bannerId
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('تم حذف البانر بنجاح');
        loadData();
      } else {
        throw new Error(data.message || 'فشل في حذف البانر');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setSelectedBanner(null);
    setDialogOpen(true);
  };

  const openEditDialog = (banner) => {
    setSelectedBanner(banner);
    setFormData({
      name: banner.name || '',
      image: banner.image || '',
      link: banner.link || '',
      active: banner.active === 1 || banner.active === '1',
      display_order: banner.display_order || 0
    });
    setImagePreview(banner.image ? `${API_BASE_URL}/api/${banner.image}` : '');
    setImageFile(null);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      image: '',
      link: '',
      active: true,
      display_order: 0
    });
    setImagePreview('');
    setImageFile(null);
    setSelectedBanner(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Navigation Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <ImageIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            إدارة البانرات
          </Typography>
          <Button
            color="inherit"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            إضافة بانر جديد
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Success/Error Messages */}
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Snackbar>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Banners Grid */}
        <Grid container spacing={3}>
          {banners.map((banner) => (
            <Grid item xs={12} sm={6} md={4} key={banner.id}>
              <Card sx={{ position: 'relative' }}>
                {/* Status Badge */}
                <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}>
                  <Chip
                    icon={banner.active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    label={banner.active ? 'نشط' : 'غير نشط'}
                    color={banner.active ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                {/* Banner Image */}
                <CardMedia
                  component="img"
                  height="200"
                  image={`${API_BASE_URL}/api/${banner.image}`}
                  alt={banner.name}
                  sx={{ objectFit: 'cover' }}
                />

                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {banner.name}
                  </Typography>
                  
                  {banner.link && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LinkIcon sx={{ fontSize: 16, mr: 1, color: '#666' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {banner.link}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <DragIcon sx={{ fontSize: 16, mr: 1, color: '#666' }} />
                    <Typography variant="body2" color="text.secondary">
                      ترتيب العرض: {banner.display_order}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => openEditDialog(banner)}
                      fullWidth
                    >
                      تعديل
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteBanner(banner.id)}
                      fullWidth
                    >
                      حذف
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {banners.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <ImageIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  لا توجد بانرات
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  انقر على "إضافة بانر جديد" لإنشاء أول بانر
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Create/Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {selectedBanner ? 'تعديل البانر' : 'إضافة بانر جديد'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="اسم البانر"
                    fullWidth
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="مثال: بانر العروض الصيفية"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="رابط البانر (اختياري)"
                    fullWidth
                    value={formData.link}
                    onChange={(e) => setFormData({...formData, link: e.target.value})}
                    placeholder="https://example.com/offer"
                    helperText="الرابط الذي سينتقل إليه المستخدم عند النقر على البانر"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="ترتيب العرض"
                    type="number"
                    fullWidth
                    value={formData.display_order}
                    onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
                    helperText="الأرقام الأقل تظهر أولاً"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.active}
                        onChange={(e) => setFormData({...formData, active: e.target.checked})}
                      />
                    }
                    label="نشط"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ 
                    border: '2px dashed #ddd', 
                    borderRadius: 2, 
                    p: 3, 
                    textAlign: 'center',
                    backgroundColor: '#fafafa'
                  }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="banner-image-upload"
                      type="file"
                      onChange={handleImageChange}
                    />
                    <label htmlFor="banner-image-upload">
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={<UploadIcon />}
                        disabled={uploading}
                      >
                        {uploading ? 'جاري الرفع...' : 'اختر صورة البانر'}
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: '#666' }}>
                      الصيغ المدعومة: JPG, PNG, GIF, WEBP (الحد الأقصى 10MB)
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ color: '#666' }}>
                      الأبعاد المقترحة: 1200x400 بكسل
                    </Typography>
                  </Box>
                </Grid>

                {imagePreview && (
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        معاينة الصورة:
                      </Typography>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '300px',
                          borderRadius: '8px',
                          border: '1px solid #ddd'
                        }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              إلغاء
            </Button>
            <Button
              onClick={selectedBanner ? handleUpdateBanner : handleCreateBanner}
              variant="contained"
              disabled={uploading || !formData.name}
            >
              {uploading ? 'جاري الحفظ...' : (selectedBanner ? 'تحديث' : 'إنشاء')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default BannerManagement;
